import React, { useEffect, useRef, useState } from 'react';
import { FaCheck, FaEdit } from 'react-icons/fa'; // Import edit and check icons from react-icons
import './EditableTranslation.css';

const EditableTranslation: React.FC<{
  translation: string;
  onSave: (newTranslation: string) => void;
}> = ({ translation, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentTranslation, setCurrentTranslation] = useState(translation);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const autoResize = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      autoResize();
    }
  }, [isEditing, currentTranslation]);

  // Toggle editing mode
  const handleEditClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsEditing(true);
  };

  // Save the edited translation
  const handleSave = () => {
    onSave(currentTranslation);
    setIsEditing(false);
  };

  // Handle input change
  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCurrentTranslation(event.target.value);
    autoResize();
  };

  // Handle blur event (clicking outside)
  const handleBlur = (event: React.FocusEvent<HTMLTextAreaElement>) => {
    event.stopPropagation();
    handleSave();
  };

  // Handle Enter key press to save changes
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSave();
    }
  };

  // Prevent parent clicks when editing
  const handleEditContainerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  // Handle confirm button click
  const handleConfirmClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleSave();
  };

  return (
    <div className="translation-container">
      {isEditing ? (
        <div className="translation-edit" onClick={handleEditContainerClick}>
          <div className="input-container">
            <textarea
              ref={textareaRef}
              value={currentTranslation}
              onChange={handleInputChange}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              autoFocus
              rows={1}
            />
            <button
              className="confirm-button-circle"
              onClick={handleConfirmClick}
              title="Save changes"
            >
              <FaCheck />
            </button>
          </div>
        </div>
      ) : (
        <>
          <span className="translation">{translation}</span>
          <div className="edit-icon-container">
            <FaEdit
              className="edit-icon"
              title="Edit"
              onClick={handleEditClick}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default EditableTranslation;
